import { Bell, User } from "@phosphor-icons/react";
import { Popover } from "radix-ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { actionsToMenuItems, separator } from "~/actions/adapters/menu";
import { useActionContext } from "~/actions/context";
import {
  keyboardShortcutsAction,
  languageAction,
  signOutAction,
} from "~/actions/definitions/settings";
import { appearanceAction } from "~/actions/definitions/theme";
import { useCurrentWorkspace, useUnreadCount, useViewer } from "~/api/queries";
import { ABILITY } from "~/api/types";
import { NotificationInbox } from "~/features/notifications/NotificationInbox";
import { Avatar } from "~/foundation/components/Avatar";
import { IconButton } from "~/foundation/components/IconButton";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { depths } from "~/foundation/styles/depths";

export interface StatusClusterProps {
  /**
   * Extension slot. Products mounted on this shell push their own status chips here
   * (sync state, build status, environment badge) without touching the console bar.
   */
  consoleStatusItems?: ReactNode[];
}

function Notifications() {
  const { t } = useTranslation();
  // W9 §A.5.2 — the bell is now the inbox. `unseen` is undefined while loading or on error,
  // which renders no dot rather than a phantom one.
  const { data: unseen } = useUnreadCount();

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <span className="relative inline-flex">
          <IconButton icon={Bell} aria-label={t("Notifications")} />
          {unseen ? (
            <span aria-hidden className="absolute end-0.5 top-0.5 size-2 rounded-full bg-accent" />
          ) : null}
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={8}
          className="slate-pop w-[340px] rounded-md border border-divider bg-menu-bg p-2 shadow-menu"
          style={{ zIndex: depths.menu }}
        >
          <NotificationInbox />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function AccountMenu() {
  const { t } = useTranslation();
  const { data: viewer } = useViewer();
  const { data: workspace } = useCurrentWorkspace();
  const context = useActionContext({ source: "contextmenu" });

  const actionItems = actionsToMenuItems(
    [
      appearanceAction,
      languageAction,
      keyboardShortcutsAction,
      separator("account"),
      signOutAction,
    ],
    context,
  );

  const items: MenuItem[] = [
    { type: "heading", title: viewer?.email ?? t("Account") },
    ...(workspace?.abilities[ABILITY.workspaceUpdate]
      ? [{ type: "route" as const, title: t("Profile"), icon: User, to: "/settings" }]
      : []),
    ...actionItems,
  ];

  return (
    <Menu
      align="end"
      ariaLabel={t("Account menu")}
      items={items}
      trigger={
        <button
          type="button"
          aria-label={t("Account menu")}
          className="inline-flex size-8 items-center justify-center rounded-full hover:bg-hover-bg"
        >
          <Avatar name={viewer?.name ?? "?"} src={viewer?.avatarUrl ?? null} size={28} />
        </button>
      }
    />
  );
}

export function StatusCluster({ consoleStatusItems = [] }: StatusClusterProps) {
  return (
    <div className="flex items-center gap-1">
      {consoleStatusItems.map((node, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: caller-supplied nodes have no id
        <div key={`status-${index}`} className="flex items-center">
          {node}
        </div>
      ))}
      <Notifications />
      <AccountMenu />
    </div>
  );
}
